import { hooks } from 'farmx-redux-core';
import { isMobile } from 'react-device-detect';
import {
  formatIrrigationDepthUnit,
  formatIrrigationDepthValue,
  roundIrrigationDepthValue,
} from './recommendationFormat';
import {
  formatRecommendationDuration,
  getRecommendationSummaryValues,
} from '../../../helper/recommendationDisplayHelper';
import { SUMMARY_GRAPH_BAR_COLOR } from '../../../constants';

// Key for ETc, ET0, and Kc labels
const dataKeyConfig = {
  etcForecastDaily: 'ETc',
  kcDaily: 'Kc',
  et0ForecastDaily: 'ET0',
};

function getBarGraphConfig(chartData, label, shouldFormatOneDecimal = false,
  minPointLength = 0) {
  const options = {
    title: null,
    chart: {
      type: 'column',
      // 75 and 25 for maintaining margin around x-axis label and chart card container.
      marginBottom: isMobile ? 75 : 25,
      marginTop: 20,
      inverted: false,
    },
    tooltip: {
      formatter: () => false,
    },
    plotOptions: {
      series: {
        borderRadius: '5px',
        groupPadding: 0,
        stacking: 'normal',
      },
      column: {
        minPointLength,
        dataLabels: {
          enabled: true,
          inside: false,
          crop: true,
          overflow: 'justify',
          color: 'grey',
          formatter() {
            const value = shouldFormatOneDecimal
              ? formatIrrigationDepthValue(this.y)
              : formatRecommendationDuration(this.y);
            return `${value} ${label}`;
          },
          style: {
            fontWeight: 'bold',
            fontSize: isMobile ? '11px':'14px',
          }
        }
      },
    },
    xAxis: chartData?.xAxis,
    yAxis: chartData?.yAxis,
    series: chartData?.series,
    credits: {
      enabled: false,
    },
    time: {
      useUTC: false,
    },
    lang: {
      noData: !chartData?.series?.length ? 'No Data' : ''
    }
  };

  return options;
}

function getValidValue(value) {
  let validValue = 0;
  if (value && isFinite(value) && !isNaN(validValue)) validValue = value;
  return validValue;
}

export const prepareSummaryBarGraphConfig = (recommendation, applicationRate,
  toUnit, userVolumeUnit, toUnitByLength, blockAreaInM2, t) => {
  const getUserUnits = hooks.useUnits();
  const isDepth = toUnit === 'depth';
  const isDuration = toUnit === 'duration';
  const isVolume = toUnit === 'volume';
  const summaryValues = getRecommendationSummaryValues({
    recommendation: recommendation?.recommendation,
    applicationRate,
    unitType: toUnit,
    userVolumeUnit,
    toUnitByLength,
    blockAreaInM2,
    getUserUnits,
  });
  const valueArr = [summaryValues.deficit, summaryValues.etcForecast,
    summaryValues.totalDeficit, summaryValues.scheduled,
    summaryValues.precipForecast, summaryValues.required];

  function buildHighChartsData() {
    const categories = [
      t('Deficit'),
      t('Forecast (ETc)'),
      t('Total Deficit'),
      t('Scheduled'),
      t('Precip Forecast'),
      t('Required'),
    ];

    const xAxis = {
      categories,
      labels: {
        style: {
          // 12px reduces the size of font for mobile view
          // 14px for desktop view
          fontSize: isMobile ? '12px' : '14px',
        },
      },
    };

    const minValue = Math.min(...valueArr);
    const maxValue = Math.max(...valueArr);
    const valueRange = maxValue - minValue;
    const axisPadding = valueRange > 0
      ? valueRange * 0.1
      : Math.max(Math.abs(maxValue), 1) * 0.1;

    const yAxis = {
      title: false,
      min: parseFloat(minValue - axisPadding),
      max: parseFloat(maxValue + axisPadding),
    };

    function getSummaryGraphPoint(value, color) {
      const y = roundIrrigationDepthValue(getValidValue(value));
      const isZeroVolume = isVolume && y === 0;
      return {
        y,
        color: isZeroVolume ? 'none' : color,
        borderColor: isZeroVolume ? 'none' : undefined,
        borderWidth: isZeroVolume ? 0 : undefined,
      };
    }

    const series = [
      {
        showInLegend: false,
        data: [
          getSummaryGraphPoint(summaryValues.deficit, SUMMARY_GRAPH_BAR_COLOR.deficit),
          getSummaryGraphPoint(summaryValues.etcForecast, SUMMARY_GRAPH_BAR_COLOR.forecast),
          getSummaryGraphPoint(summaryValues.totalDeficit,
            SUMMARY_GRAPH_BAR_COLOR.totalDeficit),
          getSummaryGraphPoint(summaryValues.scheduled, SUMMARY_GRAPH_BAR_COLOR.scheduled),
          getSummaryGraphPoint(summaryValues.precipForecast,
            SUMMARY_GRAPH_BAR_COLOR.precipitationForecast),
          getSummaryGraphPoint(summaryValues.required,
            SUMMARY_GRAPH_BAR_COLOR.requiredHours),
        ],
      },
    ];
    return {
      series,
      xAxis,
      yAxis,
    };
  }

  const chartData = buildHighChartsData(recommendation);
  const { label: depthUnitsLabel } = getUserUnits(0, 'inches',
    undefined, { decimalPlaces: 2 }, toUnitByLength);
  const options = getBarGraphConfig(
    chartData,
    !isDepth ? summaryValues.label : formatIrrigationDepthUnit(depthUnitsLabel, t),
    !isDuration,
    isVolume ? 4 : 0,
  );
  return options;
};

export function prepareLineGraphConfig(chartData, chartData1, type1, type2, chartLabel) {
  let chartDataToRender = [];
  if (chartData?.length) {
    chartDataToRender = [{
      showInLegend: false,
      data: chartData,
      name: type1,
    }];
  }
  if (chartData1?.length) {
    chartDataToRender = [...chartDataToRender, {
      showInLegend: false,
      data: chartData1,
      name: type2,
    }];
  }

  const options = {
    title: null,
    chart: {
      type: 'line',
      inverted: false,
    },
    yAxis: {
      title: false,
    },
    tooltip: {
      formatter() {
        const date = new Date(this?.x);
        const options = {
          month: 'short', day: 'numeric'
        };
        return `${date?.toLocaleDateString(undefined, options)}
        <br><b>${`${this?.series?.name} value` || 'Value'}: ${this?.y?.toFixed(2)}
         ${chartLabel}<b></br>`;
      }
    },
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        day: '%d %b'
      },
      tickPadding: 10,
      labels: {
        padding: 5
      },
    },
    plotOptions: {
      series: {
        label: {
          connectorAllowed: false
        },
      }
    },
    credits: {
      enabled: false,
    },
    series: chartDataToRender?.length ? chartDataToRender : []
  };

  return options;
}

export const prepareETcLineGraphConfig = (data, etLabel, dataKey) => {
  const etDataAndDates = data?.dateDaily?.map((date, index) => {
    const dateValue = new Date(date);
    const dateString = dateValue?.toDateString();
    return [Date.parse(dateString), data?.[dataKey]?.[index] || 0];
  });

  return prepareLineGraphConfig(etDataAndDates, undefined,
    dataKeyConfig[dataKey], undefined, etLabel);
};

export const prepareBarGraphConfig = (data, label) => {
  const xAxis = {
    type: 'datetime',
    dateTimeLabelFormats: {
      day: '%d %b'
    },
    tickPadding: 10,
    labels: {
      padding: 5
    },
  };

  const dataPoints = data?.map((d) => d?.[1] || 0);
  let yAxis;
  if (dataPoints) {
    yAxis = {
      title: false,
      min: 0,
      max: parseFloat(Math.max(...dataPoints)),
    };
  }

  const updatedData = {
    series: data?.length ? [
      {
        showInLegend: false,
        data,
      }] : null,
    xAxis,
    yAxis,
  };

  return getBarGraphConfig(updatedData, label, true);
};
